import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Wrench, MessageSquare, Brain, AlertCircle, CheckCircle2, Terminal } from 'lucide-react';
import { overlayDismiss } from '../../lib/overlayDismiss';

/**
 * BuildLogModal — 即時看 Claude Code 在幫你建 avatar 時做了什麼
 *
 * 把 SSE event stream 折成 human-readable rows：
 *   - 「報告」訊息 (text_delta 累積)
 *   - 工具呼叫 (tool_start + tool_input_delta + tool_result)
 *   - thinking（可選 toggle）
 *   - 階段事件 (started / result / error)
 */
export function BuildLogModal({ open, job, onClose }) {
    const { t } = useTranslation('ivrAgents');
    const [showThinking, setShowThinking] = useState(false);
    const scrollRef = useRef(null);
    const stickRef = useRef(true);   // 是否要 stick to bottom

    const rows = useMemo(() => deriveRows(job?.events || [], showThinking, t), [job?.events, showThinking, t]);

    // auto-scroll：rows 多了就 follow，但用戶往上滾就停
    useEffect(() => {
        if (!open) return;
        const el = scrollRef.current;
        if (!el || !stickRef.current) return;
        el.scrollTop = el.scrollHeight;
    }, [rows.length, open]);

    function handleScroll(e) {
        const el = e.currentTarget;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        stickRef.current = atBottom;
    }

    if (!open || !job) return null;

    const isDone = job.status === 'completed';
    const isFailed = job.status === 'failed';

    return (
        <div
            className="fixed inset-0 bg-ink-900/40 backdrop-blur-sm z-50 grid place-items-center p-4"
            {...overlayDismiss(onClose)}
        >
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-3xl h-[80vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-cream-200">
                    <div className="flex items-center gap-3 min-w-0">
                        <Terminal size={16} strokeWidth={1.8} className="text-accent-700 shrink-0" />
                        <div className="min-w-0">
                            <div className="heading-display text-sm font-semibold text-ink-900 truncate">
                                {job.name}
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mt-0.5">
                                {isDone ? 'completed' : isFailed ? 'failed' : 'building'} · {job.events.length} events
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="inline-flex items-center gap-1.5 text-[11px] text-ink-500 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={showThinking}
                                onChange={(e) => setShowThinking(e.target.checked)}
                                className="accent-accent-500"
                            />
                            {t('buildLog.showThinking')}
                        </label>
                        <button
                            onClick={onClose}
                            className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto px-5 py-4 space-y-2 bg-cream-100/40"
                >
                    {rows.length === 0 ? (
                        <div className="text-center text-ink-400 text-sm py-12">{t('buildLog.preparing')}</div>
                    ) : (
                        rows.map((r, i) => <RowView key={i} row={r} />)
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-2 border-t border-cream-200 font-mono text-[10px] text-ink-400 flex items-center justify-between">
                    <span>session {job.sessionId || job.jobId}</span>
                    <span>{stickRef.current ? t('buildLog.following') : t('buildLog.followPaused')}</span>
                </div>
            </div>
        </div>
    );
}

function RowView({ row }) {
    if (row.kind === 'say') {
        return (
            <div className="flex gap-2.5 items-start">
                <MessageSquare size={14} strokeWidth={1.8} className="text-accent-700 shrink-0 mt-0.5" />
                <div className="text-sm text-ink-900 leading-relaxed whitespace-pre-wrap break-words">
                    {row.text}
                </div>
            </div>
        );
    }
    if (row.kind === 'tool') {
        return (
            <div className="flex gap-2.5 items-start">
                <Wrench size={14} strokeWidth={1.8} className="text-ink-500 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                    <div className="font-mono text-[11px] text-ink-700">
                        <span className="text-accent-700">{row.name}</span>
                        {row.summary && <span className="text-ink-500"> · {row.summary}</span>}
                    </div>
                    {row.result && (
                        <div className="mt-1 font-mono text-[10px] text-ink-500 bg-cream-200/40 rounded px-2 py-1 max-h-24 overflow-y-auto whitespace-pre-wrap break-words">
                            {row.result}
                        </div>
                    )}
                </div>
            </div>
        );
    }
    if (row.kind === 'think') {
        return (
            <div className="flex gap-2.5 items-start opacity-60">
                <Brain size={14} strokeWidth={1.8} className="text-ink-400 shrink-0 mt-0.5" />
                <div className="text-[12px] text-ink-500 italic leading-relaxed whitespace-pre-wrap break-words">
                    {row.text}
                </div>
            </div>
        );
    }
    if (row.kind === 'log') {
        return (
            <div className="flex gap-2.5 items-start">
                <Terminal size={14} strokeWidth={1.8} className="text-ink-400 shrink-0 mt-0.5" />
                <div className="font-mono text-[10px] text-ink-500 whitespace-pre-wrap break-words">
                    {row.text}
                </div>
            </div>
        );
    }
    if (row.kind === 'event') {
        const Icon = row.severity === 'error' ? AlertCircle : CheckCircle2;
        const color = row.severity === 'error' ? 'text-danger-500' : 'text-green-700';
        return (
            <div className="flex gap-2.5 items-center">
                <Icon size={14} strokeWidth={1.8} className={`${color} shrink-0`} />
                <div className={`font-mono text-[11px] ${color}`}>{row.text}</div>
            </div>
        );
    }
    return null;
}

/**
 * deriveRows — 把 raw SSE events 折成顯示用 rows
 */
function deriveRows(events, showThinking, t) {
    const rows = [];
    let currentSay = null;     // 累積中的 text_delta
    let currentTool = null;    // 累積中的 tool_input_delta
    let currentThink = null;

    function flushSay() {
        if (currentSay && currentSay.text.trim()) rows.push(currentSay);
        currentSay = null;
    }
    function flushThink() {
        if (currentThink && currentThink.text.trim()) rows.push(currentThink);
        currentThink = null;
    }

    for (const ev of events) {
        switch (ev.type) {
            case 'started':
                rows.push({ kind: 'event', severity: 'ok', text: t('buildLog.row.started', { mode: ev.mode || '' }) });
                break;

            case 'text_start':
                flushSay();
                currentSay = { kind: 'say', text: '' };
                break;
            case 'text_delta':
                if (!currentSay) currentSay = { kind: 'say', text: '' };
                currentSay.text += ev.text || '';
                break;
            case 'text_stop':
                flushSay();
                break;

            case 'thinking_start':
                flushThink();
                if (showThinking) currentThink = { kind: 'think', text: '' };
                break;
            case 'thinking_delta':
                if (showThinking) {
                    if (!currentThink) currentThink = { kind: 'think', text: '' };
                    currentThink.text += ev.text || '';
                }
                break;
            case 'thinking_stop':
                flushThink();
                break;

            case 'tool_start':
                flushSay();
                flushThink();
                currentTool = { kind: 'tool', name: ev.name || 'tool', _rawInput: '', summary: '', result: '' };
                rows.push(currentTool);
                break;
            case 'tool_input_delta':
                if (currentTool) {
                    currentTool._rawInput += ev.json || '';
                    currentTool.summary = summarizeToolInput(currentTool.name, currentTool._rawInput);
                }
                break;
            case 'tool_result':
                if (currentTool) {
                    currentTool.result = truncate(typeof ev.content === 'string' ? ev.content : JSON.stringify(ev.content), 600);
                    currentTool = null;
                }
                break;

            case 'log':
                if (ev.message) {
                    rows.push({ kind: 'log', text: truncate(ev.message, 240) });
                }
                break;

            case 'result':
            case 'completed':
                flushSay();
                flushThink();
                if (ev.status === 'failed') {
                    rows.push({ kind: 'event', severity: 'error', text: t('buildLog.row.failed', { message: ev.error || ev.message || '' }) });
                } else {
                    rows.push({ kind: 'event', severity: 'ok', text: t('buildLog.row.completed', { suffix: ev.avatarId ? ' · avatarId=' + ev.avatarId : '' }) });
                }
                break;

            case 'error':
            case 'failed':
                flushSay();
                flushThink();
                rows.push({ kind: 'event', severity: 'error', text: t('buildLog.row.error', { message: ev.message || ev.error || 'error' }) });
                break;

            default:
                break;
        }
    }
    flushSay();
    flushThink();
    return rows;
}

/**
 * Try to extract a short, readable one-line summary from the streaming JSON input.
 * 不要求 JSON 完整 — 用 regex 拉常見欄位。
 */
function summarizeToolInput(name, raw) {
    if (!raw) return '';
    // Bash: extract command
    if (name === 'Bash') {
        const m = raw.match(/"command"\s*:\s*"((?:\\.|[^"\\])*)/);
        if (m) return truncate(unescapeStr(m[1]), 140);
    }
    // Read: file_path
    if (name === 'Read') {
        const m = raw.match(/"file_path"\s*:\s*"((?:\\.|[^"\\])*)/);
        if (m) return truncate(unescapeStr(m[1]), 140);
    }
    // Edit / Write: file_path
    if (name === 'Edit' || name === 'Write') {
        const m = raw.match(/"file_path"\s*:\s*"((?:\\.|[^"\\])*)/);
        if (m) return truncate(unescapeStr(m[1]), 140);
    }
    // Grep
    if (name === 'Grep') {
        const m = raw.match(/"pattern"\s*:\s*"((?:\\.|[^"\\])*)/);
        if (m) return truncate(unescapeStr(m[1]), 140);
    }
    return '';
}

function unescapeStr(s) {
    try {
        return JSON.parse('"' + s + '"');
    } catch {
        return s;
    }
}

function truncate(s, n) {
    if (!s) return '';
    return s.length > n ? s.slice(0, n) + '…' : s;
}
